import { useState, useEffect, useRef } from 'react';
import { blastApi } from "@flows/api/blast-api";
import type { FlowSummary } from "@flows/api/blast-api-types";
import { useWorkspace } from "@/context/workspace";

export interface FlowTreeNode {
  name: string;
  type: 'folder' | 'file';
  children?: FlowTreeNode[];
  flow?: FlowSummary;
}

export interface UseFlowsResult {
  flows: FlowSummary[];
  flowTree: FlowTreeNode[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Stable query key used across app to identify the flows tree
export const FLOWS_TREE_QK = ["flows", "tree"] as const;

// Lightweight invalidation mechanism for non-react-query consumers
type Listener = () => void;
const flowsTreeListeners = new Set<Listener>();
export const invalidateFlowsTree = () => {
  flowsTreeListeners.forEach((fn) => {
    try {
      fn();
    } catch {}
  });
};

function buildFlowTree(flows: FlowSummary[]): FlowTreeNode[] {
  const rootNodes: FlowTreeNode[] = [];
  const nodeMap = new Map<string, FlowTreeNode>();

  flows.forEach((flow) => {
    const parts = flow.id.split('/');
    let currentPath = '';

    parts.forEach((part, index) => {
      const isLast = index === parts.length - 1;
      const parentPath = currentPath;
      currentPath = currentPath ? `${currentPath}/${part}` : part;

      if (!nodeMap.has(currentPath)) {
        const node: FlowTreeNode = {
          name: part,
          type: isLast ? 'file' : 'folder',
          children: isLast ? undefined : [],
          flow: isLast ? flow : undefined,
        };
        nodeMap.set(currentPath, node);

        if (parentPath) {
          const parentNode = nodeMap.get(parentPath);
          if (parentNode && parentNode.children) {
            parentNode.children.push(node);
          }
        } else {
          rootNodes.push(node);
        }
      }
    });
  });

  function sortNodes(nodes: FlowTreeNode[]): void {
    nodes.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'folder' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
    nodes.forEach((node) => node.children && sortNodes(node.children));
  }

  sortNodes(rootNodes);
  return rootNodes;
}

export function useFlows(): UseFlowsResult {
  const { active, wsChangeCount } = useWorkspace();
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [flowTree, setFlowTree] = useState<FlowTreeNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchFlows = async () => {
    try {
      setIsLoading(true);
      setError(null);
      // Workspace-aware reset to avoid cross-workspace leaks
      setFlows([]);
      setFlowTree([]);
      try {
        const response = await blastApi.listFlows(active?.id || undefined);
        const flowsData = response.items;
        setFlows(flowsData);
        setFlowTree(buildFlowTree(flowsData));
      } catch (apiError) {
        console.warn('API not available, using mock data:', apiError);
        const mockFlows: FlowSummary[] = [
          { id: "executors/conditional/if/flag-check", title: "If Conditional: flag check", category: "Executors/Conditional/If", tags: ["pins:required", "conditional", "if", "boolean"], hasDefaults: true },
          { id: "executors/conditional/switch/value-routing", title: "Switch Conditional: value routing", category: "Executors/Conditional/Switch", tags: ["pins:required", "conditional", "switch", "numeric"], hasDefaults: true },
          { id: "executors/loop/dowhile/double-until-50", title: "Do-While Loop: double value until >= 50", category: "Executors/Loop/DoWhile", tags: ["pins:required", "loop", "dowhile", "threshold", "doubling"], hasDefaults: true },
          { id: "executors/loop/dowhile/value-doubling", title: "Do-While Loop: value doubling until threshold", category: "Executors/Loop/DoWhile", tags: ["pins:required", "loop", "dowhile", "threshold", "doubling"], hasDefaults: true },
          { id: "executors/loop/fixed/increment-3", title: "Fixed Loop: increment 3 times", category: "Executors/Loop/Fixed", tags: ["pins:required", "loop", "fixed", "increment"], hasDefaults: true },
          { id: "executors/loop/while/convergent-math", title: "While Loop: mathematical convergence", category: "Executors/Loop/While", tags: ["pins:required", "loop", "while", "convergence", "mathematical"], hasDefaults: true },
          { id: "patterns/threshold-routing", title: "Threshold Routing Pattern", category: "Patterns", tags: ["pins:required", "pattern", "conditional", "threshold", "routing"], hasDefaults: true },
        ];
        setFlows(mockFlows);
        setFlowTree(buildFlowTree(mockFlows));
      }
    } catch (err) {
      console.error('Failed to fetch flows:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch flows');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Refetch when workspace changes
    fetchFlows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id, wsChangeCount]);

  // Subscribe to invalidation events to refresh the tree
  useEffect(() => {
    mountedRef.current = true;
    const listener: Listener = () => {
      if (mountedRef.current) void fetchFlows();
    };
    flowsTreeListeners.add(listener);
    return () => {
      mountedRef.current = false;
      flowsTreeListeners.delete(listener);
    };
  }, []);

  return { flows, flowTree, isLoading, error, refetch: fetchFlows };
}
